import {
  formatDateRange,
  formatMonthYear,
} from '../../../../ts-utils/date-utils'
import type {
  OfficialTypeNameLookup,
  TeamHistoryFields,
  TeamHistoryOfficial,
  TeamHistoryOfficialView,
  TeamHistoryTerms,
  TeamHistoryView,
} from '../types'
import { countLabel } from './countLabel'

interface BuildTeamHistoryOptions {
  terms: TeamHistoryTerms
  officialTypeName: OfficialTypeNameLookup
}

const initialsOf = (first: string, last: string): string =>
  [first, last].map((part) => part.trim().charAt(0).toUpperCase()).join('')

const toOfficialView = (
  official: TeamHistoryOfficial,
  officialTypeName: OfficialTypeNameLookup
): TeamHistoryOfficialView => ({
  id: official.id,
  fullName: [official.name_first, official.name_last]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' '),
  initials: initialsOf(official.name_first, official.name_last),
  roleName: officialTypeName(official.team_official_type_id),
  isTreasurer: official.is_treasurer,
  image: official.image,
})

/**
 * Turns the team-official endpoint's history fields into display-ready labels.
 *
 * Returns `null` when the team carries no history at all (`seasons_played`
 * absent) — the player endpoint shares the `Team` shape without these fields,
 * and the caller then falls back to the plain team row.
 */
export const buildTeamHistory = (
  team: TeamHistoryFields,
  { terms, officialTypeName }: BuildTeamHistoryOptions
): TeamHistoryView | null => {
  if (typeof team.seasons_played !== 'number') {
    return null
  }

  const competitionCount = team.seasons_played
  const playerCount = team.player_count ?? null
  const last = team.last_competition ?? null

  return {
    hasPlayed: competitionCount > 0,
    organizationName: team.organization?.name || undefined,
    competitionCount,
    competitionCountLabel: countLabel(competitionCount, terms.competition),
    playerCount,
    playerCountLabel:
      playerCount === null ? '' : countLabel(playerCount, terms.player),
    firstPlayedLabel: formatMonthYear(team.first_played_at),
    lastPlayedLabel: formatMonthYear(team.last_played_at),
    lastCompetition: last
      ? {
          name: last.name,
          organizationName: last.organization?.name || undefined,
          dateRangeLabel: formatDateRange(last.starts_at, last.ends_at),
        }
      : null,
    officials: (team.team_officials ?? []).map((official) =>
      toOfficialView(official, officialTypeName)
    ),
  }
}
